import { AfterViewInit, Component, OnInit, OnDestroy, inject } from '@angular/core';
import { genTinyGUID, SawDust } from '@cudirect/shared-ui-library';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { InputStatus } from '@workflow/directive/input-mask/input-mask-processing.base';
import { maskInput } from '@workflow/directive/input-mask/mask.helper';
import {
  LenderEligibilityQuestionnaire,
  LenderEligibilityQuestion,
  LenderEligibilityQuestionField
} from '@workflow/ecp-workflow/dynamic-app/types/dynamic-eligibility-questions';
import { getValidationObject } from '@workflow/helper/validation-converter/validation-converter';

/** This component dyanmically generates membership eligibility questions. */
@BuildingBlock('generate-eligibility')
@Component({
  selector: 'ecp-dynamic-eligibility',
  templateUrl: './dynamic-eligibility.component.html'
})
export class DynamicEligibilityComponent extends BuildingBlockControlBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  protected logger = inject(SawDust).withContext({ component: 'DynamicEligibilityComponent' });

  /** Membership Eligibility Questionnaire for the lender */
  public dynamicEligibilityQuestionnaire: LenderEligibilityQuestionnaire = {};
  /** Eligibility Questions currently selected */
  public questionnaireSelection: string;
  /** Questions Fields */
  public dynamicEligibilityQuestions: LenderEligibilityQuestion[];
  /** API prefix */
  public apiPrefix: string;
  /** last state of mobile number input */
  private lastState: InputStatus;
  /** isOlaFlow */
  public isOlaFlow = false;
  /** isOAOFlow */
  public isOAOFlow = false;
  /** This needs to be a class property because we need to use it to track if logic has been run  */
  private meEventPairId: string;
  /** Used for tracking click action on "No" - need to track this at the class level, so we can destroy it at the end */
  private radioElement: HTMLElement;

  public ngOnInit(): void {
    this.isOlaFlow = this.workflowService?.currentFlow?.id === 'ola' || this.workflowService?.currentFlow?.id.indexOf('ola') !== -1;
    this.isOAOFlow = this.workflowService?.currentFlow?.id === 'oao' || this.workflowService?.currentFlow?.id.indexOf('oao') !== -1;
    this.apiPrefix = this.field.config.storageId.split('/')[0];
    this.dynamicEligibilityQuestions = new Array<LenderEligibilityQuestion>();
    this.getDynamicEligibilityQuestionnaire();
  }

  public ngAfterViewInit(): void {
    this.setUpMEClickListener();
  }

  /**
   * Since we don't have much control on when "no" radio button is clicked in WW (it is dynamically generated)...
   * we need to set up a simple js click listener here that is attached to the "No" radio button.
   * TODO: This is very brittle... We need to loop back around and refactor...
   * We should be leveraging Angular and the WW framework, SEE #221749
   */
  public setUpMEClickListener(): void {
    const getEl = (id) => document.getElementById(id);
    this.radioElement = getEl('ola-member-is-lender-employee-0') || getEl('oao-member-is-lender-employee-0'); // get ola or oao radio
    if (this.radioElement) this.radioElement.addEventListener('click', this.radioClickHandler.bind(this));
  }

  /**
   * Upon false click, lets start the clock for UEM data load
   * @param event - The click event
   */
  public radioClickHandler(event) {
    if (event.target.value === 'on') this.meTrackingEventHandler('start');
  }

  /** show Eligibility Questions */
  public get showEligibilityQuestions(): boolean {
    if (this.isOlaFlow) {
      const isMember = this.statefulService.wrap('ola:olaApplicationInformation/membership/isMember').value;
      return isMember === false;
    }

    if (this.isOAOFlow) {
      const isMember = this.statefulService.wrap('api:oaoMemberInformation/alreadyMember').value;
      return isMember === false;
    }
    return false;
  }

  /**
   * Handles Membership Eligibility (ME) tracking events.
   * @remarks The method of tracking here is the distance between the user clicking "no I am not a member" and the data being loaded
   * Therefore, the result of this comparison *could be a negative integer*, if the data loads before no is selected
   * In the above event, UEM will show it as 0ms.
   *
   * @param state - Indicates the state of the tracking event. Either 'start' or 'end'.
   * @param result - Optional param - result of the tracking event, either 'success' or 'error'. This is only used when the state is 'end'.
   */
  private meTrackingEventHandler(state: 'start' | 'end', result?: 'success' | 'error') {
    /** We need to track if the pairing should be closed out, since end can happen before start */
    let closePairing;
    if (!this.meEventPairId) this.meEventPairId = genTinyGUID(24);
    else closePairing = true;

    // Is this a start or finish event?
    state === 'start'
      ? this.logEvent('ecp.ui.dynamicEligibilityQuestions', 'start', this.meEventPairId)
      : this.logEvent('ecp.ui.dynamicEligibilityQuestions', 'end', this.meEventPairId, result);

    // If we're closing the event, lets clear the identifier - need to support start>end or end>start
    if (closePairing) this.meEventPairId = null;
  }

  private getDynamicEligibilityQuestionnaire(): void {
    const questionnare = this.statefulService.wrap(`${this.apiPrefix}/dynamicEligibility/questionnare`);
    if (questionnare.value) {
      this.dynamicEligibilityQuestionnaire = JSON.parse(questionnare.valueStringOnly);
      this.questionnaireSelection = this.statefulService.wrap(`${this.apiPrefix}/dynamicEligibility/selectedQuestion`).valueString;
      this.getQuestionsFromBundle();
      this.checkValidation();
      this.meTrackingEventHandler('end', 'success');
    } else {
      const cachedEligibility = this.l360Service.cachedEligibility;
      if (cachedEligibility) {
        this.dynamicEligibilityQuestionnaire = cachedEligibility;
        this.saveDataToBundle();
        this.checkValidation();
        this.l360Service.cachedEligibility = null;
        return;
      }
      this.getQuestionnaireFromSource();
    }
  }

  private getQuestionsFromBundle(): void {
    if (this.statefulService.arraySize(`${this.apiPrefix}/dynamicEligibility/questions/$/question`) === 0) {
      return;
    }
    if (this.dynamicEligibilityQuestionnaire?.questions) {
      for (let index = 0; index < this.dynamicEligibilityQuestionnaire?.questions?.length; index++) {
        if (index === 0) {
          const firstQuestion: LenderEligibilityQuestion[] = [
            JSON.parse(this.statefulService.wrap(`${this.apiPrefix}/dynamicEligibility/questions/$/question`, index).valueStringOnly)
          ];
          this.dynamicEligibilityQuestions = firstQuestion;
        } else {
          this.dynamicEligibilityQuestions.push(
            JSON.parse(this.statefulService.wrap(`${this.apiPrefix}/dynamicEligibility/questions/$/question`, index).valueStringOnly)
          );
        }
      }
    }
  }

  public getQuestionnaireFromSource(): void {
    this.statefulService.wrap('mem:show-card-spinner').value = true;
    this.l360Service.getDynamicEligibilityQuestions().subscribe(
      (response) => {
        if (response?.isSuccess) {
          this.l360Service.cachedEligibility = response.data;
          this.dynamicEligibilityQuestionnaire = response.data;
          this.saveDataToBundle();
          this.checkValidation();
          this.statefulService.wrap('mem:show-card-spinner').value = false;
          this.meTrackingEventHandler('end', 'success');
        } else {
          this.meTrackingEventHandler('end', 'error');
          console.error(response);
        }
      },
      (err) => {
        this.statefulService.wrap('mem:show-card-spinner').value = false;
        this.checkValidation();
        this.meTrackingEventHandler('end', 'error');
        console.error(err);
      }
    );
  }

  private saveDataToBundle(): void {
    this.statefulService.wrap(`${this.apiPrefix}/dynamicEligibility/questionnare`).value = JSON.stringify(
      this.dynamicEligibilityQuestionnaire
    );

    if (this.dynamicEligibilityQuestionnaire?.questions) {
      this.statefulService.arrayTruncate(`${this.apiPrefix}/dynamicEligibility/questions`, () => {
        this.dynamicEligibilityQuestionnaire?.questions.forEach((question, index) => {
          this.dynamicEligibilityQuestions.push(question);
          this.statefulService.arrayAdd(`${this.apiPrefix}/dynamicEligibility/questions`, () => {
            this.statefulService.wrap(`${this.apiPrefix}/dynamicEligibility/questions/$/question`, index).value = JSON.stringify(question);
          });
        });
      });
    }
  }

  public getQuestionnaireLabelText(): string {
    const questionnaireLabel = this.dynamicEligibilityQuestionnaire?.label;
    if (questionnaireLabel?.toLowerCase() === 'default') {
      return 'See if you qualify through one of the following criteria:';
    }
    return questionnaireLabel;
  }

  public hasQuestionFieldDropdown(questionField: LenderEligibilityQuestionField): boolean {
    return questionField?.type?.toLowerCase() === 'dropdown';
  }

  public hasQuestionFieldRadio(questionField: LenderEligibilityQuestionField): boolean {
    return questionField?.type?.toLowerCase() === 'radio' && questionField?.option?.items?.length === 0;
  }

  public hasQuestionFieldRadioList(questionField: LenderEligibilityQuestionField): boolean {
    return questionField?.type?.toLowerCase() === 'radio' && questionField?.option?.items?.length > 0;
  }

  public hasQuestionFieldTextBox(questionField: LenderEligibilityQuestionField): boolean {
    return questionField?.type?.toLowerCase() === 'input';
  }

  public hasQuestionFieldNumber(questionField: LenderEligibilityQuestionField): boolean {
    return questionField?.type?.toLowerCase() === 'number';
  }

  public hasQuestionFieldDate(questionField: LenderEligibilityQuestionField): boolean {
    return questionField?.type?.toLowerCase() === 'date';
  }

  public selectionChanged(): void {
    this.statefulService.wrap(`${this.apiPrefix}/dynamicEligibility/selectedQuestion`).value = this.questionnaireSelection;
    this.checkValidation();
  }

  public questionAnswerChanged(question: LenderEligibilityQuestion, questionIndex: number, fieldIndex: number): void {
    const field = question.questionFields[fieldIndex];
    // eslint-disable-next-line eqeqeq
    if (field.type == 'dropdown') {
      if (question.questionFields.length > 0) {
        const selectedLookupItemID = question.questionFields[fieldIndex].selectedLookupItemID;
        question.questionFields[fieldIndex].option.items.forEach((item) => {
          if (item.lookupItemID.toString() === selectedLookupItemID.toString()) {
            question.questionFields[fieldIndex].value = item.value;
            return;
          }
        });
      }
    }
    this.statefulService.wrap(`${this.apiPrefix}/dynamicEligibility/questions/$/question`, questionIndex).value = JSON.stringify(question);
    this.checkValidation();
  }

  public questionAnswerForDateField(question: LenderEligibilityQuestion, questionIndex: number, questionFieldIndex: number): void {
    this.questionAnswerChanged(question, questionIndex, questionFieldIndex);
  }

  private checkValidation(): void {
    if (!this.questionnaireSelection || !this.dynamicEligibilityQuestionnaire || !this.dynamicEligibilityQuestions) {
      this.field.data.value = null;
      return;
    }
    let areValidAnswers = true;
    this.dynamicEligibilityQuestions
      .find((question) => question.id === this.questionnaireSelection)
      .questionFields.forEach((questionField) => {
        if (questionField.isRequired && !questionField.value) {
          areValidAnswers = false;
        }
      });
    if (areValidAnswers) {
      this.field.data.value = 'true';
    } else {
      this.field.data.value = null;
    }
  }

  /** methods helps to process mask */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public processMask(inputElement: any): void {
    const mask = maskInput('date', this.readState(inputElement), this.lastState, getValidationObject(this.field));
    if (mask.changed) {
      inputElement.target.value = mask.processed.value;
      if (inputElement.target.value) {
        inputElement.target.setSelectionRange(mask.processed.selection[0], mask.processed.selection[1]);
      }
      inputElement.target.value = mask.processed.value;
    }
    this.lastState = this.readState(inputElement);
  }

  /** Read inputelement state */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readState(inputElement: any): InputStatus {
    const domElement = inputElement.target;
    return {
      focused: this.focused,
      selection: [domElement.selectionStart, domElement.selectionEnd],
      value: inputElement.target.value
    };
  }

  private logEvent(eventName: string, eventState: 'start' | 'end', correlationId: string, status?: 'success' | 'error'): void {
    this.applicationInsightsService.logEvent(eventName, {
      eventState,
      correlationId,
      status
    });
    this.logger.logEvent(eventName, {
      eventState,
      correlationId,
      status
    });
  }

  ngOnDestroy() {
    if (this.radioElement) this.radioElement.removeEventListener('click', this.radioClickHandler.bind(this));
  }
}
